import { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "../lib/api";
import { fmtIDR, fmtDate, today } from "../lib/format";
import { PageHeader, Card, Badge, Modal, Field, Btn, DataTable, inputCls, statusTone } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

function ItemLines({ items, lines, setLines, withPrice = true, testidPrefix }) {
  return (
    <div className="space-y-2">
      {lines.map((l, i) => (
        <div key={i} className={`grid ${withPrice ? "grid-cols-[1fr_90px_130px_32px]" : "grid-cols-[1fr_90px_32px]"} gap-2 items-center`}>
          <select className={inputCls} value={l.item_id} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, item_id: e.target.value } : x))} data-testid={`${testidPrefix}-item-${i}`}>
            <option value="">Pilih barang...</option>
            {items.map((it) => <option key={it.id} value={it.id}>{it.code} - {it.name}</option>)}
          </select>
          <input type="number" placeholder="Qty" className={inputCls} value={l.qty || ""} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, qty: +e.target.value } : x))} data-testid={`${testidPrefix}-qty-${i}`} />
          {withPrice && <input type="number" placeholder="Harga" className={inputCls} value={l.price || ""} onChange={(e) => setLines(lines.map((x, xi) => xi === i ? { ...x, price: +e.target.value } : x))} data-testid={`${testidPrefix}-price-${i}`} />}
          <button className="text-slate-600 hover:text-rose-400" onClick={() => setLines(lines.filter((_, xi) => xi !== i))}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <Btn tone="ghost" className="!text-xs" onClick={() => setLines([...lines, { item_id: "", qty: 0, price: 0 }])} data-testid={`${testidPrefix}-add-line`}><Plus className="w-3.5 h-3.5" /> Baris</Btn>
    </div>
  );
}

export default function Purchase() {
  const { hasPerm, projects } = useAuth();
  const [tab, setTab] = useState("pr");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [prs, setPrs] = useState([]);
  const [pos, setPos] = useState([]);
  const [receives, setReceives] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showPR, setShowPR] = useState(false);
  const [showPO, setShowPO] = useState(false);
  const [showRCV, setShowRCV] = useState(false);
  const [showINV, setShowINV] = useState(false);
  const [showPAY, setShowPAY] = useState(null);
  const [prForm, setPrForm] = useState({ date: today(), notes: "", project_id: "", items: [{ item_id: "", qty: 0, price: 0 }] });
  const [poForm, setPoForm] = useState({ pr_id: "", vendor: "", date: today(), notes: "", project_id: "", items: [{ item_id: "", qty: 0, price: 0 }] });
  const [rcvForm, setRcvForm] = useState({ po_id: "", warehouse_id: "", date: today(), items: [] });
  const [invForm, setInvForm] = useState({ po_id: "", date: today(), due_date: today(), vendor_invoice_no: "" });
  const [payForm, setPayForm] = useState({ date: today(), amount: 0 });
  const [prDetail, setPrDetail] = useState(null);

  const openPrDetail = async (pid) => {
    try {
      const r = await api.get(`/purchase/pr/${pid}/detail`);
      setPrDetail(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onPoPrChange = async (prId) => {
    setPoForm((f) => ({ ...f, pr_id: prId || null }));
    if (!prId) return;
    try {
      const r = await api.get(`/purchase/pr/${prId}/detail`);
      setPrDetail(r.data);
      setPoForm((f) => ({ ...f, pr_id: prId, project_id: r.data.pr.project_id || "",
        items: r.data.items.map((i) => ({ item_id: i.item_id, qty: i.qty, price: i.price || 0 })) }));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    api.get("/inventory/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/inventory/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
    api.get("/purchase/pr").then((r) => setPrs(r.data));
    api.get("/purchase/po").then((r) => setPos(r.data));
    api.get("/purchase/receives").then((r) => setReceives(r.data));
    api.get("/purchase/invoices").then((r) => setInvoices(r.data));
    api.get("/purchase/payments").then((r) => setPayments(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const wrap = (fn, okMsg, closer) => async () => {
    try {
      const res = await fn();
      if (res?.data?.status === "pending_approval") toast.info(res.data.message);
      else toast.success(okMsg);
      closer && closer();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [docDetail, setDocDetail] = useState(null);
  const [editPr, setEditPr] = useState(null);

  const openDocDetail = async (type, id) => {
    try {
      const r = await api.get(`/purchase/${type}/${id}/detail`);
      setDocDetail({ type, data: r.data });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = (path, label) => wrap(() => api.delete(path), label);

  const openEditPr = (pr) => {
    setEditPr(pr);
    setPrForm({ date: pr.date, notes: pr.notes || "", project_id: pr.project_id || "",
      items: pr.items.map((i) => ({ item_id: i.item_id, qty: i.qty, price: i.price || 0 })) });
    setShowPR(true);
  };

  const savePr = wrap(() => editPr
      ? api.put(`/purchase/pr/${editPr.id}`, { ...prForm, project_id: prForm.project_id || null, items: prForm.items.filter((i) => i.item_id && i.qty > 0) })
      : api.post("/purchase/pr", { ...prForm, project_id: prForm.project_id || null, items: prForm.items.filter((i) => i.item_id && i.qty > 0) }),
    editPr ? "PR diperbarui" : "PR dibuat", () => { setShowPR(false); setEditPr(null); });

  const approvedPOs = pos.filter((p) => ["approved", "partial_received"].includes(p.status));
  const unpaidInvoices = invoices.filter((i) => i.status !== "paid");

  const openRcv = (po) => {
    setRcvForm({ po_id: po.id, warehouse_id: warehouses[0]?.id || "", date: today(), items: po.items.filter((i) => i.received_qty < i.qty).map((i) => ({ item_id: i.item_id, qty: i.qty - i.received_qty })) });
    setShowRCV(true);
  };

  const TABS = [["pr", "PR"], ["po", "PO"], ["receive", "Penerimaan"], ["invoice", "Faktur"], ["payment", "Pembayaran"]];

  return (
    <div data-testid="purchase-page">
      <PageHeader title="Purchase" subtitle="Siklus: PR → PO → Penerimaan Barang → Faktur → Pembayaran (terintegrasi Akuntansi & Inventory)" testid="purchase-header">
        {hasPerm("purchase", "create") && (
          <>
            <Btn tone="ghost" onClick={() => { setEditPr(null); setPrForm({ date: today(), notes: "", project_id: "", items: [{ item_id: "", qty: 0, price: 0 }] }); setShowPR(true); }} data-testid="add-pr-button"><Plus className="w-4 h-4" /> PR</Btn>
            <Btn onClick={() => setShowPO(true)} data-testid="add-po-button"><Plus className="w-4 h-4" /> PO</Btn>
          </>
        )}
      </PageHeader>

      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`purchase-tab-${k}`}
            className={`text-xs sm:text-sm px-3.5 py-2 rounded-lg border transition-colors ${tab === k ? "bg-emerald-600/10 text-emerald-300 border-emerald-500/50" : "text-slate-400 border-slate-800 hover:bg-slate-900 hover:text-slate-200"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "pr" && (
        <DataTable testid="pr-table" searchKeys={["number", "requested_by"]}
          columns={[
            { header: "No. PR", accessor: "number", mono: true },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Pemohon", accessor: "requested_by" },
            { header: "Item", render: (r) => <span className="text-xs text-slate-400">{r.items.map((i) => `${i.item_name} (${i.qty})`).join(", ")}</span> },
            { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
            { header: "Aksi", render: (r) => (
              <div className="flex gap-1.5">
                <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => openDocDetail("pr", r.id)} data-testid={`pr-detail-${r.number}`}>Detail</Btn>
                {r.status === "draft" && hasPerm("purchase", "edit") && (
                  <>
                    <Btn tone="amber" className="!px-2.5 !py-1 !text-xs" onClick={() => openEditPr(r)} data-testid={`pr-edit-${r.number}`}>Edit</Btn>
                    <Btn className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put(`/purchase/pr/${r.id}/status`, { status: "approved" }), "PR disetujui")} data-testid={`pr-approve-${r.number}`}>Setujui</Btn>
                  </>
                )}
                {hasPerm("purchase", "delete") && r.status !== "converted" && (
                  <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={del(`/purchase/pr/${r.id}`, "PR dihapus")} data-testid={`pr-delete-${r.number}`}>Hapus</Btn>
                )}
              </div>
            ) },
          ]}
          rows={prs} empty="Belum ada Purchase Requisition" />
      )}

      {tab === "po" && (
        <DataTable testid="po-table" searchKeys={["number", "vendor"]}
          columns={[
            { header: "No. PO", accessor: "number", mono: true },
            { header: "Vendor", accessor: "vendor" },
            { header: "Ref PR", accessor: "pr_number", mono: true },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Total", render: (r) => <span className="font-mono text-xs text-emerald-400">{fmtIDR(r.total)}</span> },
            { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status.replace(/_/g, " ")}</Badge> },
            { header: "Aksi", render: (r) => (
              <div className="flex gap-1.5">
                <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => openDocDetail("po", r.id)} data-testid={`po-detail-${r.number}`}>Detail</Btn>
                {r.status === "draft" && hasPerm("purchase", "edit") && <Btn className="!px-2.5 !py-1 !text-xs" onClick={wrap(() => api.put(`/purchase/po/${r.id}/submit`), "PO diajukan")} data-testid={`po-submit-${r.number}`}><Send className="w-3 h-3" /> Ajukan</Btn>}
                {["approved", "partial_received"].includes(r.status) && hasPerm("purchase", "create") && <Btn className="!px-2.5 !py-1 !text-xs" onClick={() => openRcv(r)} data-testid={`po-receive-${r.number}`}>Terima</Btn>}
                {["approved", "partial_received", "received"].includes(r.status) && hasPerm("purchase", "create") && !invoices.some((i) => i.po_id === r.id) && (
                  <Btn tone="amber" className="!px-2.5 !py-1 !text-xs" onClick={() => { setInvForm({ po_id: r.id, date: today(), due_date: today(), vendor_invoice_no: "" }); setShowINV(true); }} data-testid={`po-invoice-${r.number}`}>Faktur</Btn>
                )}
                {hasPerm("purchase", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={del(`/purchase/po/${r.id}`, "PO dihapus")} data-testid={`po-delete-${r.number}`}>Hapus</Btn>}
              </div>
            ) },
          ]}
          rows={pos} empty="Belum ada Purchase Order" />
      )}

      {tab === "receive" && (
        <DataTable testid="receive-table" searchKeys={["number", "po_number"]}
          columns={[
            { header: "No. RCV", accessor: "number", mono: true },
            { header: "PO", accessor: "po_number", mono: true },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Item Diterima", render: (r) => <span className="text-xs text-slate-400">{r.items.map((i) => `${i.item_name} (${i.qty})`).join(", ")}</span> },
            { header: "Oleh", accessor: "created_by" },
            { header: "Aksi", render: (r) => (
              <div className="flex gap-1.5">
                <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => openDocDetail("receives", r.id)} data-testid={`rcv-detail-${r.number}`}>Detail</Btn>
                {hasPerm("purchase", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={del(`/purchase/receives/${r.id}`, "Penerimaan dihapus, stok dikembalikan")} data-testid={`rcv-delete-${r.number}`}>Hapus</Btn>}
              </div>
            ) },
          ]}
          rows={receives} empty="Belum ada penerimaan barang" />
      )}

      {tab === "invoice" && (
        <DataTable testid="pinvoice-table" searchKeys={["number", "vendor"]}
          columns={[
            { header: "No. Faktur", accessor: "number", mono: true },
            { header: "Vendor", accessor: "vendor" },
            { header: "Jatuh Tempo", render: (r) => fmtDate(r.due_date) },
            { header: "Total", render: (r) => <span className="font-mono text-xs">{fmtIDR(r.total)}</span> },
            { header: "Terbayar", render: (r) => <span className="font-mono text-xs text-emerald-400">{fmtIDR(r.paid)}</span> },
            { header: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
            { header: "Aksi", render: (r) => (
              <div className="flex gap-1.5">
                <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => openDocDetail("invoices", r.id)} data-testid={`pinv-detail-${r.number}`}>Detail</Btn>
                {r.status !== "paid" && hasPerm("purchase", "create") && (
                  <Btn className="!px-2.5 !py-1 !text-xs" onClick={() => { setShowPAY(r); setPayForm({ date: today(), amount: r.total - r.paid }); }} data-testid={`pinv-pay-${r.number}`}>Bayar</Btn>
                )}
                {hasPerm("purchase", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={del(`/purchase/invoices/${r.id}`, "Faktur dihapus beserta jurnalnya")} data-testid={`pinv-delete-${r.number}`}>Hapus</Btn>}
              </div>
            ) },
          ]}
          rows={invoices} empty="Belum ada faktur pembelian" />
      )}

      {tab === "payment" && (
        <DataTable testid="ppayment-table" searchKeys={["number", "vendor"]}
          columns={[
            { header: "No. Voucher", accessor: "number", mono: true },
            { header: "Faktur", accessor: "invoice_number", mono: true },
            { header: "Vendor", accessor: "vendor" },
            { header: "Tanggal", render: (r) => fmtDate(r.date) },
            { header: "Jumlah", render: (r) => <span className="font-mono text-xs text-emerald-400">{fmtIDR(r.amount)}</span> },
            { header: "Aksi", render: (r) => (
              <div className="flex gap-1.5">
                <Btn tone="ghost" className="!px-2.5 !py-1 !text-xs" onClick={() => openDocDetail("payments", r.id)} data-testid={`ppay-detail-${r.number}`}>Detail</Btn>
                {hasPerm("purchase", "delete") && <Btn tone="danger" className="!px-2.5 !py-1 !text-xs" onClick={del(`/purchase/payments/${r.id}`, "Pembayaran dihapus beserta jurnalnya")} data-testid={`ppay-delete-${r.number}`}>Hapus</Btn>}
              </div>
            ) },
          ]}
          rows={payments} empty="Belum ada pembayaran" />
      )}

      <Modal open={showPR} onClose={() => { setShowPR(false); setEditPr(null); }} title={editPr ? `Edit ${editPr.number}` : "Purchase Requisition (PR)"} wide testid="pr-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tanggal"><input type="date" className={inputCls} value={prForm.date} onChange={(e) => setPrForm({ ...prForm, date: e.target.value })} data-testid="pr-date-input" /></Field>
            <Field label="Proyek (Cost Center)">
              <select className={inputCls} value={prForm.project_id} onChange={(e) => setPrForm({ ...prForm, project_id: e.target.value })} data-testid="pr-project-select">
                <option value="">Global / Kantor Pusat</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Catatan"><input className={inputCls} value={prForm.notes} onChange={(e) => setPrForm({ ...prForm, notes: e.target.value })} data-testid="pr-notes-input" /></Field>
          <ItemLines items={items} lines={prForm.items} setLines={(v) => setPrForm({ ...prForm, items: v })} withPrice={false} testidPrefix="pr" />
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => { setShowPR(false); setEditPr(null); }}>Batal</Btn>
            <Btn onClick={savePr} data-testid="pr-save-button">{editPr ? "Simpan Perubahan" : "Simpan PR"}</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showPO} onClose={() => setShowPO(false)} title="Purchase Order (PO)" wide testid="po-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Vendor"><input className={inputCls} value={poForm.vendor} onChange={(e) => setPoForm({ ...poForm, vendor: e.target.value })} data-testid="po-vendor-input" /></Field>
            <Field label="Tanggal"><input type="date" className={inputCls} value={poForm.date} onChange={(e) => setPoForm({ ...poForm, date: e.target.value })} data-testid="po-date-input" /></Field>
          </div>
          <Field label="Ref PR (opsional)">
            <select className={inputCls} value={poForm.pr_id} onChange={(e) => onPoPrChange(e.target.value)} data-testid="po-pr-select">
              <option value="">Tanpa PR</option>
              {prs.filter((p) => p.status === "approved").map((p) => <option key={p.id} value={p.id}>{p.number}</option>)}
            </select>
          </Field>
          {prDetail && poForm.pr_id && (
            <div className="bg-slate-950 border border-cyan-500/30 rounded-lg p-3" data-testid="po-pr-detail-panel">
              <div className="text-[10px] font-mono uppercase tracking-wider text-cyan-400 mb-2">Detail PR {prDetail.pr.number} — Pemohon: {prDetail.pr.requested_by} · Lokasi: {prDetail.location_name || "Global"}</div>
              <div className="space-y-1">
                {prDetail.items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-slate-300">{i.item_name} × {i.qty} {i.unit}</span>
                    <span className={`font-mono ${i.stock_total <= i.min_stock ? "text-rose-400" : "text-emerald-400"}`}>stok: {i.stock_total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Field label="Proyek (Cost Center)">
            <select className={inputCls} value={poForm.project_id} onChange={(e) => setPoForm({ ...poForm, project_id: e.target.value })} data-testid="po-project-select">
              <option value="">Global / Kantor Pusat</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <ItemLines items={items} lines={poForm.items} setLines={(v) => setPoForm({ ...poForm, items: v })} testidPrefix="po" />
          <div className="text-right text-sm font-mono text-emerald-400" data-testid="po-total-display">
            Total: {fmtIDR(poForm.items.reduce((s, i) => s + (i.qty || 0) * (i.price || 0), 0))}
          </div>
          <p className="text-[11px] text-slate-500">PO dengan total ≥ Rp 50.000.000 akan otomatis masuk antrian approval Direktur.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowPO(false)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/purchase/po", { ...poForm, project_id: poForm.project_id || null, items: poForm.items.filter((i) => i.item_id && i.qty > 0) }), "PO dibuat", () => setShowPO(false))} data-testid="po-save-button">Simpan PO</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showRCV} onClose={() => setShowRCV(false)} title="Penerimaan Barang" wide testid="rcv-form-modal">
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Gudang Tujuan">
              <select className={inputCls} value={rcvForm.warehouse_id} onChange={(e) => setRcvForm({ ...rcvForm, warehouse_id: e.target.value })} data-testid="rcv-warehouse-select">
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </Field>
            <Field label="Tanggal"><input type="date" className={inputCls} value={rcvForm.date} onChange={(e) => setRcvForm({ ...rcvForm, date: e.target.value })} data-testid="rcv-date-input" /></Field>
          </div>
          <ItemLines items={items} lines={rcvForm.items} setLines={(v) => setRcvForm({ ...rcvForm, items: v })} withPrice={false} testidPrefix="rcv" />
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowRCV(false)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/purchase/receives", { ...rcvForm, items: rcvForm.items.filter((i) => i.item_id && i.qty > 0) }), "Barang diterima, stok bertambah", () => setShowRCV(false))} data-testid="rcv-save-button">Simpan Penerimaan</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={showINV} onClose={() => setShowINV(false)} title="Faktur Pembelian" testid="pinv-form-modal">
        <div className="space-y-3">
          <Field label="No. Faktur Vendor"><input className={inputCls} value={invForm.vendor_invoice_no} onChange={(e) => setInvForm({ ...invForm, vendor_invoice_no: e.target.value })} data-testid="pinv-vendor-no" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Tanggal"><input type="date" className={inputCls} value={invForm.date} onChange={(e) => setInvForm({ ...invForm, date: e.target.value })} data-testid="pinv-date" /></Field>
            <Field label="Jatuh Tempo"><input type="date" className={inputCls} value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} data-testid="pinv-due" /></Field>
          </div>
          <p className="text-[11px] text-slate-500">Jurnal otomatis: Dr Persediaan Material / Cr Hutang Usaha.</p>
          <div className="flex justify-end gap-2 pt-2">
            <Btn tone="ghost" onClick={() => setShowINV(false)}>Batal</Btn>
            <Btn onClick={wrap(() => api.post("/purchase/invoices", invForm), "Faktur dibuat & dijurnal", () => setShowINV(false))} data-testid="pinv-save-button">Simpan Faktur</Btn>
          </div>
        </div>
      </Modal>

      <Modal open={!!showPAY} onClose={() => setShowPAY(null)} title={showPAY ? `Bayar Faktur ${showPAY.number}` : ""} testid="ppay-form-modal">
        {showPAY && (
          <div className="space-y-3">
            <div className="text-sm text-slate-400">Sisa tagihan: <span className="font-mono text-slate-100">{fmtIDR(showPAY.total - showPAY.paid)}</span></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Tanggal"><input type="date" className={inputCls} value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} data-testid="ppay-date" /></Field>
              <Field label="Jumlah"><input type="number" className={inputCls} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: +e.target.value })} data-testid="ppay-amount" /></Field>
            </div>
            <p className="text-[11px] text-slate-500">Jurnal otomatis: Dr Hutang Usaha / Cr Kas Besar.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Btn tone="ghost" onClick={() => setShowPAY(null)}>Batal</Btn>
              <Btn onClick={wrap(() => api.post("/purchase/payments", { invoice_id: showPAY.id, ...payForm }), "Pembayaran dicatat & dijurnal", () => setShowPAY(null))} data-testid="ppay-save-button">Bayar</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!docDetail} onClose={() => setDocDetail(null)} title="Detail Dokumen" wide testid="purchase-detail-modal">
        {docDetail && (() => {
          const d = docDetail.data;
          const main = d.pr || d.po || d.receive || d.invoice || d.payment || {};
          const skipKeys = new Set(["items", "id", "payload"]);
          const links = [["Lokasi/Cost Center", d.location_name], ["Ref PR", d.pr_number], ["Pemohon PR", d.pr_requested_by],
                         ["PO", d.po_number], ["Vendor", d.vendor], ["Gudang", d.warehouse_name], ["No. Jurnal", d.journal_number]]
            .filter(([, v]) => v);
          const itemRows = d.items || d.po_items || [];
          return (
            <div className="space-y-4">
              <div className="space-y-1.5">
                {Object.entries(main).filter(([k, v]) => !skipKeys.has(k) && typeof v !== "object").map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 text-sm border-b border-slate-800/60 py-1.5">
                    <span className="text-slate-500 text-xs uppercase tracking-wide">{k.replace(/_/g, " ")}</span>
                    <span className="text-slate-200 text-right font-mono text-xs">{typeof v === "number" ? new Intl.NumberFormat("id-ID").format(v) : String(v ?? "-")}</span>
                  </div>
                ))}
                {links.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 text-sm border-b border-slate-800/60 py-1.5">
                    <span className="text-cyan-500/80 text-xs uppercase tracking-wide">{k}</span>
                    <span className="text-slate-200 text-right font-mono text-xs">{String(v)}</span>
                  </div>
                ))}
              </div>
              {itemRows.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-800">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-slate-900 border-b border-slate-800">
                      {Object.keys(itemRows[0]).map((k) => <th key={k} className="text-left font-mono uppercase tracking-wider text-slate-500 px-3 py-2">{k}</th>)}
                    </tr></thead>
                    <tbody>
                      {itemRows.map((it, i) => (
                        <tr key={i} className="border-b border-slate-800/60">
                          {Object.values(it).map((v, vi) => <td key={vi} className="px-3 py-2 text-slate-300 font-mono">{String(v ?? "-")}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {d.receives && <div className="text-xs text-slate-400">Penerimaan terkait: {d.receives.length} dokumen ({d.receives.map((r) => r.number).join(", ") || "-"})</div>}
              {d.payments && <div className="text-xs text-slate-400">Pembayaran terkait: {d.payments.length} ({d.payments.map((p) => p.number).join(", ") || "-"})</div>}
              {d.approval && <div className="text-xs text-slate-400">Approval: {d.approval.status} — {d.approval.levels.map((l) => `${l.role}:${l.status}${l.signed_by ? ` (${l.signed_by})` : ""}`).join(" → ")}</div>}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
